import { User, ChevronDown, LogOut } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { LanguageSwitcher } from '../i18n';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';
import { AppDropdown } from './AppDropdown';
import { logOut } from '../firebase/config';

interface HeaderProps {
  onLogout?: () => void;
  onNavigate?: (path: string) => void;
}

export const Header = ({ onLogout, onNavigate }: HeaderProps) => {
  const { user, userProfile } = useAuth();
  const { t } = useTranslation('shared');

  const handleLogout = async () => {
    try {
      await logOut();
      if (onLogout) {
        onLogout();
      }
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const getUserName = () => {
    if (userProfile?.display_name) {
      return userProfile.display_name;
    }
    if (user?.email) {
      return user.email.split('@')[0];
    }
    return user?.isAnonymous ? 'Guest User' : 'User';
  };

  return (
    <header className="fixed top-0 left-0 right-0 bg-white border-b border-gray-200 px-6 h-16 z-[60]">
      <div className="flex h-full items-center justify-between">
        {/* Left Section */}
        <div className="flex items-center space-x-4">
          <WorkspaceSwitcher onNavigate={onNavigate} />
          <AppDropdown />
        </div>

        {/* Right Section */}
        <div className="flex items-center space-x-3">
          <LanguageSwitcher />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center space-x-2">
                <User className="w-4 h-4" />
                <span>{getUserName()}</span>
                <ChevronDown className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <div className="px-3 py-2 border-b">
                <p className="font-medium text-sm">{getUserName()}</p>
                <p className="text-xs text-gray-500">{user?.email || 'No email'}</p>
              </div>
              <DropdownMenuItem onClick={handleLogout}>
                <LogOut className="w-4 h-4 mr-2" />
                {t('header.signOut')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};
